import React, { useEffect, useMemo, useState } from 'react';
import AutoGraphRoundedIcon from '@mui/icons-material/AutoGraphRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import RadarRoundedIcon from '@mui/icons-material/RadarRounded';
import { Alert, Box, Chip, CircularProgress, Container, Paper, Stack, Typography } from '@mui/material';
import './asset-scanner-premium.css';

import ScannerHeader from '../../components/scanner/ScannerHeader';
import ScannerFilters from '../../components/scanner/ScannerFilters';
import AssetTable from '../../components/scanner/AssetTable';
import AssetQuickView from '../../components/scanner/AssetQuickView';

import AssetHeatmap from '../../components/scanner/AssetHeatmap';
import AIAssetInsight from '../../components/scanner/AIAssetInsight';
import PortfolioDashboard from '../portfolio/PortfolioDashboard';
import TopOpportunities from '../../components/scanner/TopOpportunities';

const fallbackAssets = [
  { symbol: 'BTC', name: 'Bitcoin', price: 70000, perf24h: 2.1, perf7d: 5.2, volume: 32000000000, volatility: 4, momentumScore: 85, trendStrength: 8, riskScore: 3, compositeScore: 90 },
  { symbol: 'ETH', name: 'Ethereum', price: 3500, perf24h: 1.8, perf7d: 4.7, volume: 18000000000, volatility: 5, momentumScore: 78, trendStrength: 7, riskScore: 4, compositeScore: 82 },
  { symbol: 'SOL', name: 'Solana', price: 180, perf24h: 3.2, perf7d: 8.1, volume: 4000000000, volatility: 7, momentumScore: 92, trendStrength: 9, riskScore: 6, compositeScore: 88 },
];

const getAssetKey = (asset) => asset?.code || asset?.symbol || asset?.name || '';

const SectionIntro = ({ eyebrow, title, description }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="overline" sx={{ color: '#6d7e84', letterSpacing: '0.12em' }}>
      {eyebrow}
    </Typography>
    <Typography variant="h5" sx={{ fontWeight: 800, color: '#17323c', mb: 0.7 }}>
      {title}
    </Typography>
    <Typography variant="body2" sx={{ color: '#556970', lineHeight: 1.8, maxWidth: 760 }}>
      {description}
    </Typography>
  </Box>
);

const AssetScannerPage = () => {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({});
  const [selectedAsset, setSelectedAsset] = useState(null);

  useEffect(() => {
    async function fetchMetrics() {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch('/api/asset-metrics/latest');
        if (!res.ok) throw new Error('Erreur réseau');
        const data = await res.json();
        setAssets(Array.isArray(data) ? data : []);
      } catch (e) {
        setError('Impossible de récupérer les métriques. Merci de réessayer plus tard.');
        setAssets([]);
      } finally {
        setLoading(false);
      }
    }
    fetchMetrics();
  }, []);

  const usingMockData = !loading && assets.length === 0;
  const displayAssets = useMemo(() => (assets.length ? assets : fallbackAssets), [assets]);
  const effectiveSelectedAsset = selectedAsset || displayAssets[0] || null;
  const topAsset = displayAssets[0] || null;

  useEffect(() => {
    if (!displayAssets.length) {
      if (selectedAsset !== null) {
        setSelectedAsset(null);
      }
      return;
    }

    const selectedKey = getAssetKey(selectedAsset);
    const stillExists = displayAssets.some((asset) => getAssetKey(asset) === selectedKey);

    if (!selectedKey || !stillExists) {
      setSelectedAsset(displayAssets[0]);
    }
  }, [displayAssets, selectedAsset]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'radial-gradient(circle at top left, rgba(228,197,114,0.18) 0%, rgba(228,197,114,0) 28%), linear-gradient(180deg, #f8f4ec 0%, #f2ede3 52%, #ebe5db 100%)',
        py: { xs: 3, md: 5 },
      }}
    >
      <Container maxWidth="xl">
        <Paper
          elevation={0}
          sx={{
            overflow: 'hidden',
            borderRadius: 6,
            p: { xs: 3, md: 5 },
            mb: 4,
            background: 'linear-gradient(135deg, #10252b 0%, #214550 38%, #43736f 100%)',
            color: '#f8f4ec',
          }}
        >
          <Stack direction={{ xs: 'column', xl: 'row' }} spacing={3} justifyContent="space-between" alignItems="stretch">
            <Box sx={{ maxWidth: 760 }}>
              <Chip
                label="Asset Scanner"
                sx={{ mb: 2, bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', letterSpacing: '0.08em', fontWeight: 700 }}
              />
              <Typography variant="h3" sx={{ fontWeight: 800, mb: 1.5, maxWidth: 760 }}>
                Scanner les actifs comme une salle de contrôle, pas comme une table brute
              </Typography>
              <Typography variant="body1" sx={{ color: 'rgba(248,244,236,0.82)', maxWidth: 700, lineHeight: 1.75 }}>
                Cette page priorise la lecture rapide des opportunités, du contexte marché et du détail actif. La heatmap, la table, le quick view et l’insight IA restent présents, mais dans une hiérarchie plus lisible et plus cohérente avec le reste du produit.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 3 }}>
                <Chip
                  label={`${displayAssets.length} actifs en lecture`}
                  sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }}
                />
                <Chip
                  label={usingMockData || error ? 'Source simulée' : 'Source API live'}
                  sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }}
                />
                {effectiveSelectedAsset ? (
                  <Chip
                    label={`Actif focus: ${getAssetKey(effectiveSelectedAsset)}`}
                    sx={{ bgcolor: 'rgba(248,244,236,0.12)', color: '#f8f4ec', fontWeight: 700 }}
                  />
                ) : null}
              </Stack>
            </Box>

            <Paper
              elevation={0}
              sx={{
                p: 3,
                borderRadius: 5,
                background: 'rgba(248,244,236,0.08)',
                border: '1px solid rgba(248,244,236,0.14)',
                backdropFilter: 'blur(10px)',
                minWidth: { xl: 360 },
              }}
            >
              <Stack spacing={2.1}>
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <RadarRoundedIcon sx={{ mt: 0.2, color: '#f3c65c' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8f4ec' }}>
                      Vue d’opportunités priorisées
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.7 }}>
                      Le haut de page met en avant l’état du scanner et les meilleurs setups du moment avant les détails tabulaires.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <AutoGraphRoundedIcon sx={{ mt: 0.2, color: '#8fd6b1' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8f4ec' }}>
                      Heatmap et quick view couplés
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.7 }}>
                      La sélection d’un actif nourrit directement la lecture détaillée et la synthèse IA dans la colonne latérale.
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1.4} alignItems="flex-start">
                  <InsightsRoundedIcon sx={{ mt: 0.2, color: '#f8f4ec' }} />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#f8f4ec' }}>
                      Contexte portefeuille embarqué
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(248,244,236,0.76)', lineHeight: 1.7 }}>
                      Le bloc portefeuille reste présent en bas de page pour remettre les signaux actifs dans une lecture d’ensemble.
                    </Typography>
                  </Box>
                </Stack>
              </Stack>
            </Paper>
          </Stack>
        </Paper>

        {error ? (
          <Alert severity="warning" sx={{ mb: 3, borderRadius: 3 }}>
            {error} Un jeu de données simulé reste affiché pour conserver la lecture de la page.
          </Alert>
        ) : usingMockData ? (
          <Alert severity="info" sx={{ mb: 3, borderRadius: 3 }}>
            Aucune métrique live n’a été remontée. La page affiche un scénario simulé pour garder le scanner exploitable visuellement.
          </Alert>
        ) : null}

        {loading ? (
          <Paper
            elevation={0}
            sx={{
              py: 6,
              px: 3,
              borderRadius: 5,
              border: '1px solid rgba(16,37,43,0.08)',
              background: 'linear-gradient(180deg, #ffffff 0%, #fbf7ef 100%)',
            }}
          >
            <Stack spacing={2} alignItems="center">
              <CircularProgress sx={{ color: '#17323c' }} />
              <Typography variant="h6" sx={{ fontWeight: 800, color: '#17323c' }}>
                Chargement des actifs du scanner
              </Typography>
              <Typography variant="body2" sx={{ color: '#556970', textAlign: 'center', maxWidth: 520 }}>
                Le scanner récupère les métriques les plus récentes avant d’afficher la heatmap, la table détaillée et les opportunités du moment.
              </Typography>
            </Stack>
          </Paper>
        ) : (
          <div className="asset-scanner-shell">
            <div className="asset-scanner-grid asset-scanner-grid--top">
              <Box sx={{ minWidth: 0 }}>
                <SectionIntro
                  eyebrow="Scanner Status"
                  title="Vue d’ensemble du flux analysé"
                  description="Ce bloc garde la synthèse du scanner visible en permanence: taille du jeu de données, cadence de rafraîchissement et état général de la lecture courante."
                />
                <ScannerHeader assetCount={displayAssets.length} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <SectionIntro
                  eyebrow="Top Setups"
                  title="Opportunités du moment"
                  description="Les signaux les plus intéressants restent isolés ici pour éviter de les noyer dans la masse de la table complète."
                />
                <TopOpportunities />
              </Box>
            </div>

            <Box sx={{ minWidth: 0 }}>
              <SectionIntro
                eyebrow="Filters"
                title="Filtrer l’univers surveillé"
                description="Les filtres servent à resserrer rapidement le scanner autour de la performance, du momentum, de la volatilité ou d’un secteur particulier."
              />
              <ScannerFilters filters={filters} setFilters={setFilters} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <SectionIntro
                eyebrow="Heatmap"
                title="Carte rapide des scores composites"
                description="La heatmap donne une lecture instantanée des 40 premiers actifs. Un clic sur une cellule alimente directement le panneau de détail."
              />
              <AssetHeatmap
                assets={displayAssets}
                onSelect={setSelectedAsset}
                selectedSymbol={getAssetKey(effectiveSelectedAsset)}
              />
            </Box>

            <div className="asset-scanner-grid asset-scanner-grid--main">
              <Box sx={{ minWidth: 0 }}>
                <SectionIntro
                  eyebrow="Detailed Table"
                  title="Classement détaillé des actifs"
                  description="La table reste le cœur opératoire du scanner avec recherche, tri, pagination et sélection rapide d’un actif."
                />
                <AssetTable
                  assets={displayAssets}
                  filters={filters}
                  onSelect={setSelectedAsset}
                  selectedSymbol={getAssetKey(effectiveSelectedAsset)}
                />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <SectionIntro
                  eyebrow="Focus"
                  title={effectiveSelectedAsset ? `${getAssetKey(effectiveSelectedAsset)} en détail` : 'Focus actif'}
                  description="Le quick view et l’insight IA gardent une lecture latérale compacte pour analyser un actif sans quitter la table principale."
                />
                <div className="asset-scanner-sidebar-stack">
                  <AssetQuickView asset={effectiveSelectedAsset || topAsset} />
                  <AIAssetInsight selectedAsset={effectiveSelectedAsset || topAsset} />
                </div>
              </Box>
            </div>

            <Box sx={{ minWidth: 0 }}>
              <SectionIntro
                eyebrow="Portfolio Context"
                title="Relier le scanner au portefeuille"
                description="Cette zone replace les opportunités actives dans un contexte portefeuille plus compact, pour éviter de raisonner le scanner en vase clos."
              />
              <PortfolioDashboard />
            </Box>
          </div>
        )}
      </Container>
    </Box>
  );
};

export default AssetScannerPage;
